var _a;
import { jsx, jsxs } from "react/jsx-runtime";
import { P as PropTypes } from "./index-qdNxdI8l.js";
import { Box, Slide } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { BREAKPOINT_DIMENSIONS, DesignSystemProvider } from "@quickenloans-ui-suite/design-system";
import { styled } from "@mui/material/styles";
import { B as Button } from "./Button-C3WhTBAE.js";
import { D as DRAWER_TRANSITION_DURATION_DESKTOP, z as CONTAINER_DATA_TEST_ID } from "./index-Dm8TmbQW.js";
import { a as isWindowDefined } from "./index-CzdAPYVv.js";
import { g as getEnvVariable, E as ENVIRONMENTS } from "./index-DF2r5-bd.js";
const GRID_AREAS = {
  ILLUSTRATION: "illustration",
  HEADER: "header",
  CTA: "cta"
};
const StyledBox = styled(Box)(({ theme }) => {
  var _a2, _b, _c, _d;
  return {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: ((_b = (_a2 = theme == null ? void 0 : theme.vars) == null ? void 0 : _a2.zIndex) == null ? void 0 : _b.bottomDrawer) ?? 1200,
    padding: theme.spacing(2, 0, 6, 0),
    background: (_d = (_c = theme == null ? void 0 : theme.vars) == null ? void 0 : _c.palette) == null ? void 0 : _d.grey[0],
    borderRadius: "10px 10px 0 0",
    boxShadow: "0px -10px 48px -16px rgba(51, 51, 51, 0.10)",
    color: "rgba(0, 0, 0, 0.88)",
    [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.medium)]: {
      padding: theme.spacing(4, 0, 7, 0),
      borderRadius: "unset"
    }
  };
});
const BannerLayoutContainer = styled(Box)(({ theme }) => ({
  ...theme.grid.getGridWrapper()
}));
const CloseContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  marginBottom: theme.spacing(1),
  [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.medium)]: {
    marginBottom: 0,
    padding: theme.spacing(0, 4)
  }
}));
const ContentContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "82px 1fr",
  gridTemplateAreas: `
    "${GRID_AREAS.ILLUSTRATION} ${GRID_AREAS.HEADER}"
    "${GRID_AREAS.CTA} ${GRID_AREAS.CTA}"
  `,
  gap: theme.spacing(2, 1.5),
  justifySelf: "center",
  "img": {
    gridArea: GRID_AREAS.ILLUSTRATION,
    width: "100%",
    aspectRatio: "1 / 1",
    objectFit: "contain",
    alignSelf: "center"
  },
  [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.medium)]: {
    gridTemplateColumns: "148px 1fr",
    gridTemplateAreas: `
    "${GRID_AREAS.ILLUSTRATION} ${GRID_AREAS.HEADER}"
    "${GRID_AREAS.ILLUSTRATION} ${GRID_AREAS.CTA}"
    `,
    gap: theme.spacing(3, 4)
  }
}));
const TitleText = styled("p")(({ theme }) => ({
  margin: 0,
  ...theme.typography.h5SemiBoldMobile,
  [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.medium)]: {
    ...theme.typography.h3SemiBold
  }
}));
const SubtitleText = styled("p")(({ theme }) => ({
  margin: 0,
  ...theme.typography.bodySmallRegularMobile,
  [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.medium)]: {
    ...theme.typography.bodyBaseRegular
  }
}));
const HeaderContainer = styled(Box)(({ theme }) => ({
  gridArea: GRID_AREAS.HEADER,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: theme.spacing(1)
}));
const CtaContainer = styled(Box)(({ theme }) => ({
  gridArea: GRID_AREAS.CTA,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.medium)]: {
    flexDirection: "row",
    alignSelf: "end"
  }
}));
const slideUpBannerDefaults = {
  imageSrc: "https://content.quickenloans.com/ql/brand/illustrations/nest_-_hand_show.svg",
  title: "Ready to find a lender?",
  subtitle: "Quicken Loans can get you connected!",
  ctaRefinance: {
    label: "Take out cash or refinance"
  },
  ctaPurchase: {
    label: "Purchase a home"
  }
};
const ActualCloseIcon = ((_a = CloseIcon) == null ? void 0 : _a.default) ?? CloseIcon;
const SlideUpBanner = ({
  isVisible,
  setIsVisible,
  onTransitionEnter = () => {
  },
  onTransitionExited = () => {
  },
  onNavigate = () => {
  },
  sourceId,
  environment = ENVIRONMENTS.PRODUCTION,
  dataTestId = "slide-up-banner"
}) => {
  const onClose = () => {
    setIsVisible(false);
  };
  const ctaRefinanceUrl = getEnvVariable({
    currentEnv: environment,
    keyToSearch: "REFINANCE_BASE_URL"
  });
  const ctaPurchaseUrl = getEnvVariable({
    currentEnv: environment,
    keyToSearch: "PURCHASE_BASE_URL"
  });
  const handleNavigate = async (url) => {
    if (onNavigate) {
      await onNavigate();
    }
    if (!isWindowDefined()) return;
    const urlWithSource = url.includes("?") ? `${url}&sourceid=${sourceId}` : `${url}?sourceid=${sourceId}`;
    window.open(urlWithSource, "_blank");
  };
  return /* @__PURE__ */ jsx(
    DesignSystemProvider,
    {
      appName: "slide-up-banner",
      theme: {},
      shouldMergeWithBaseTheme: true,
      importCss: true,
      children: /* @__PURE__ */ jsx(
        Slide,
        {
          "data-testid": CONTAINER_DATA_TEST_ID,
          direction: "up",
          in: isVisible,
          mountOnEnter: true,
          onEnter: onTransitionEnter,
          onExited: onTransitionExited,
          timeout: DRAWER_TRANSITION_DURATION_DESKTOP,
          unmountOnExit: true,
          children: /* @__PURE__ */ jsx(
            StyledBox,
            {
              "aria-modal": "false",
              role: "dialog",
              sx: { top: "unset" },
              children: /* @__PURE__ */ jsxs(BannerLayoutContainer, { children: [
                /* @__PURE__ */ jsx(CloseContainer, { children: /* @__PURE__ */ jsx(
                  Button,
                  {
                    variant: "small",
                    type: "icon",
                    onClick: onClose,
                    iconLeft: /* @__PURE__ */ jsx(ActualCloseIcon, {}),
                    "data-testid": `${dataTestId}-close-button`,
                    "aria-label": "Close Slide Up Banner"
                  }
                ) }),
                /* @__PURE__ */ jsxs(ContentContainer, { children: [
                  /* @__PURE__ */ jsx(
                    "img",
                    {
                      src: slideUpBannerDefaults.imageSrc,
                      "data-testid": `${dataTestId}-image`,
                      loading: "lazy",
                      "aria-hidden": "true"
                    }
                  ),
                  /* @__PURE__ */ jsxs(HeaderContainer, { children: [
                    /* @__PURE__ */ jsx(TitleText, { "data-testid": `${dataTestId}-title-text`, children: slideUpBannerDefaults.title }),
                    /* @__PURE__ */ jsx(SubtitleText, { "data-testid": `${dataTestId}-subtitle-text`, children: slideUpBannerDefaults.subtitle })
                  ] }),
                  /* @__PURE__ */ jsxs(CtaContainer, { children: [
                    /* @__PURE__ */ jsx(
                      Button,
                      {
                        variant: "small",
                        type: "outline",
                        onClick: () => {
                          onClose();
                          handleNavigate(ctaRefinanceUrl);
                        },
                        "data-testid": `${dataTestId}-cta-refinance-button`,
                        children: slideUpBannerDefaults.ctaRefinance.label
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      Button,
                      {
                        variant: "small",
                        type: "outline",
                        onClick: () => {
                          onClose();
                          handleNavigate(ctaPurchaseUrl);
                        },
                        "data-testid": `${dataTestId}-cta-purchase-button`,
                        children: slideUpBannerDefaults.ctaPurchase.label
                      }
                    )
                  ] })
                ] })
              ] })
            }
          )
        }
      )
    }
  );
};
SlideUpBanner.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  setIsVisible: PropTypes.func,
  onTransitionEnter: PropTypes.func,
  onTransitionExited: PropTypes.func,
  onNavigate: PropTypes.func,
  sourceId: PropTypes.string,
  environment: PropTypes.string,
  dataTestId: PropTypes.string
};
const index = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  SlideUpBanner
}, Symbol.toStringTag, { value: "Module" }));
export {
  SlideUpBanner as S,
  index as i
};
//# sourceMappingURL=index-Doy0HHoI.js.map
